import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import ContactSection from '../../components/ContactSection';
import { ArrowLeft, Calendar, Clock, PoundSterling, TrendingUp, CheckCircle2 } from 'lucide-react';

const MatterportScanCostPage = () => {
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'How Much Does a Matterport Scan Cost in the UK? 2026 Pricing Guide',
      description: 'A transparent breakdown of Matterport 3D scanning costs in the UK. Understand pricing factors, what is included, and how to calculate your return on investment.',
      author: { '@type': 'Organization', name: 'Digital Scanning Group' },
      publisher: {
        '@type': 'Organization',
        name: 'Digital Scanning Group',
        logo: { '@type': 'ImageObject', url: 'https://www.digitalscanninggroup.com/logo.png' },
      },
      datePublished: '2026-04-14',
      dateModified: '2026-04-14',
      mainEntityOfPage: 'https://www.digitalscanninggroup.com/blog/how-much-does-matterport-scan-cost-uk',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does a Matterport scan cost in the UK?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Matterport scan costs in the UK typically range from £295 for small spaces (up to 100 sq m) to £2,000 or more for large commercial properties. The price depends on floor area, number of floors, complexity, and location. Most residential and small commercial scans fall between £295 and £600.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is included in a Matterport scan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A standard Matterport scan includes on-site capture, cloud processing, a hosted 3D virtual tour, schematic floor plans, highlight reel video, measurement tools, and 12 months of Matterport hosting. Additional deliverables such as point clouds, BIM models, and branded tours are available at extra cost.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is a Matterport scan worth the investment?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'For most commercial applications, a Matterport scan offers strong return on investment. Estate agents report properties with virtual tours sell up to 31% faster. Construction teams using digital records can reduce site revisits by 50% or more. Healthcare trusts using 3D scans for estate surveys save thousands in consultant fees.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does a Matterport scan take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A typical Matterport scan takes 1-3 hours on site for a standard commercial building. Larger properties may require a full day. Processing and delivery of the final virtual tour usually takes 3-5 working days, though expedited turnaround is available.',
          },
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.digitalscanninggroup.com' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.digitalscanninggroup.com/blog' },
        { '@type': 'ListItem', position: 3, name: 'Matterport Scan Cost UK', item: 'https://www.digitalscanninggroup.com/blog/how-much-does-matterport-scan-cost-uk' },
      ],
    },
  ];

  const pricingTiers = [
    { size: 'Small (up to 100 sq m)', examples: 'Studio apartment, small retail unit, single office', price: '£295 - £450', scans: '30-60 scans' },
    { size: 'Medium (100-500 sq m)', examples: 'Restaurant, gym, office floor, care home wing', price: '£450 - £850', scans: '60-200 scans' },
    { size: 'Large (500-2,000 sq m)', examples: 'Hotel, school building, warehouse, hospital ward', price: '£850 - £1,500', scans: '200-500 scans' },
    { size: 'Very Large (2,000+ sq m)', examples: 'Shopping centre, university campus, NHS hospital', price: '£1,500 - £2,000+', scans: '500+ scans' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="How Much Does a Matterport Scan Cost in the UK? 2026 Pricing Guide"
        description="Transparent Matterport 3D scanning prices in the UK from £295. Understand what affects cost, what's included, and how to calculate ROI for your project."
        keywords="matterport scan cost uk, matterport pricing, 3D scan cost, matterport price uk, virtual tour cost, 3D scanning price, matterport survey cost"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/blog/how-much-does-matterport-scan-cost-uk` : ''}
        ogType="article"
        structuredData={structuredData}
      />
      <Navbar scrolled={true} />

      <article className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-8">
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Link>

          <header className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Pricing</span>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Calendar className="h-4 w-4" />
                14 April 2026
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Clock className="h-4 w-4" />
                7 min read
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How Much Does a Matterport Scan Cost in the UK?
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              One of the most common questions we receive is about the cost of a <Link to="/matterport-virtual-tours-london" className="text-blue-600 hover:underline">Matterport 3D scan</Link>. The honest answer is that it depends on several factors -- but this guide will give you a clear, transparent understanding of UK pricing so you can budget with confidence.
            </p>
          </header>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What Affects the Cost of a Matterport Scan?</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Several factors influence the price of a Matterport 3D scan. Understanding these will help you get an accurate quotation and ensure there are no surprises when the invoice arrives.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Floor area</strong> is the primary cost driver. Larger spaces require more scan positions, more time on site, and more processing time in the cloud. A small retail unit might require 30-40 scan positions, whilst a large hospital building could need 500 or more. Each scan position captures a 360-degree view of the surrounding space, and positions are typically placed every 2-3 metres to ensure complete coverage.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Complexity</strong> also plays a role. Open-plan spaces are quicker to scan than buildings with many small rooms, corridors, and staircases. A warehouse of 1,000 sq m will be significantly faster (and therefore cheaper) to scan than a Victorian terraced building of the same floor area with dozens of individual rooms across multiple floors.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Location</strong> matters too. Projects in central London or other major cities are straightforward for us to reach, but sites in remote locations may incur additional travel charges. We are based in London and cover the entire UK, with most projects in the South East incurring no travel surcharge.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Access restrictions</strong> can increase costs. If scanning needs to take place outside normal working hours, in secure environments requiring enhanced DBS checks, or in operational buildings where rooms must be scanned around occupants, additional time and coordination may be required.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Typical Matterport Scan Prices in the UK</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                The following table provides indicative pricing for Matterport 3D scanning services in the UK. These figures are based on standard commercial projects and include on-site capture, cloud processing, and delivery of the final virtual tour.
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="text-left p-4 font-bold text-gray-900 border-b-2 border-blue-200">Property Size</th>
                      <th className="text-left p-4 font-bold text-gray-900 border-b-2 border-blue-200">Typical Examples</th>
                      <th className="text-left p-4 font-bold text-gray-900 border-b-2 border-blue-200">Approx. Scans</th>
                      <th className="text-left p-4 font-bold text-gray-900 border-b-2 border-blue-200">Price Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingTiers.map((tier, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-4 text-gray-900 font-medium border-b border-gray-100">{tier.size}</td>
                        <td className="p-4 text-gray-600 border-b border-gray-100">{tier.examples}</td>
                        <td className="p-4 text-gray-600 border-b border-gray-100">{tier.scans}</td>
                        <td className="p-4 text-blue-600 font-bold border-b border-gray-100">{tier.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-gray-600 leading-relaxed">
                These prices are exclusive of VAT and are intended as a guide. Every project is different, and we provide detailed quotations based on your specific requirements. For multi-site or repeat projects, we offer volume discounts that can reduce the per-scan cost significantly.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What Is Included in the Price?</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                When you commission a Matterport scan from Digital Scanning Group, the standard package includes a comprehensive set of deliverables designed to provide maximum value from your investment.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {[
                  'Professional on-site capture by a trained technician',
                  'Cloud processing and model compilation',
                  'Hosted 3D virtual tour with shareable link',
                  'Schematic floor plans (PDF and PNG)',
                  'Highlight reel video for marketing',
                  'Measurement tools within the 3D model',
                  'MatterTags for points of interest',
                  '12 months of Matterport cloud hosting',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Additional deliverables are available at extra cost and can be added to any project. These include high-accuracy point cloud exports (E57 or LAS format), Revit BIM models, CAD floor plans to scale, branded virtual tours with custom logos and colour schemes, and integration with property listing platforms.
              </p>
              <p className="text-gray-600 leading-relaxed">
                For <Link to="/construction" className="text-blue-600 hover:underline">construction projects</Link> requiring higher geometric accuracy, we may recommend laser scanning instead of, or in addition to, Matterport capture. Laser scanning provides sub-millimetre accuracy and is better suited to <Link to="/blog/bim-level-2-requirements-uk" className="text-blue-600 hover:underline">BIM Level 2</Link> workflows and complex coordination tasks.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How Does DSG Compare to Other Providers?</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                The Matterport scanning market in the UK varies considerably in terms of pricing and quality. At the lower end, some providers offer basic scans for as little as £150-£200, but these often come with limitations: lower-quality equipment, no professional floor plans, limited hosting, and no ongoing support.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                At the higher end, some enterprise-focused firms charge significantly more than our published rates, often bundling services that may not be needed for every project. At Digital Scanning Group, we aim to provide professional-grade results at fair, transparent prices.
              </p>
              <p className="text-gray-600 leading-relaxed">
                What sets us apart is our experience across multiple sectors. We have scanned everything from <Link to="/healthcare" className="text-blue-600 hover:underline">NHS neonatal units</Link> and operating theatres to large commercial developments and <Link to="/leisure" className="text-blue-600 hover:underline">leisure venues</Link>. This breadth of experience means we understand the specific requirements and sensitivities of each environment, and we deliver accordingly.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Calculating Your Return on Investment</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                The cost of a Matterport scan should be evaluated against the value it delivers. For many applications, the ROI is both substantial and measurable.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Estate agents</strong> report that properties marketed with 3D virtual tours receive 49% more qualified leads and sell up to 31% faster than those without. If a virtual tour helps sell a property even one week sooner, the savings in mortgage payments, insurance, and marketing costs far exceed the cost of the scan.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Construction teams</strong> using <Link to="/blog/what-is-a-digital-twin" className="text-blue-600 hover:underline">digital twins</Link> for progress monitoring typically reduce site revisits by 50% or more. At £200-£500 per visit for a consultant or architect, a scan that prevents just two or three unnecessary visits has already paid for itself.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Facilities managers</strong> with access to accurate 3D records can resolve space queries, plan maintenance works, and brief contractors without commissioning new measured surveys each time. Over a five-year period, the savings from a single comprehensive scan can be ten times the original investment.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Healthcare trusts</strong> using 3D scans for six-facet surveys and estate condition assessments report significant reductions in surveying costs, particularly across large multi-building estates where traditional measured surveys would be prohibitively expensive.
              </p>
            </section>

            {/* FAQ Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              <div className="space-y-6">
                {[
                  { q: 'How much does a Matterport scan cost in the UK?', a: 'Prices typically range from £295 for small spaces (up to 100 sq m) to £2,000 or more for large commercial properties. The price depends on floor area, number of floors, complexity, and location.' },
                  { q: 'What is included in a Matterport scan?', a: 'A standard scan includes on-site capture, cloud processing, a hosted 3D virtual tour, schematic floor plans, highlight reel video, measurement tools, and 12 months of Matterport hosting.' },
                  { q: 'Is a Matterport scan worth the investment?', a: 'For most commercial applications, yes. Properties with virtual tours sell up to 31% faster. Construction teams can reduce site revisits by 50%. Healthcare trusts save thousands in consultant fees.' },
                  { q: 'How long does a Matterport scan take?', a: 'A typical scan takes 1-3 hours on site for a standard commercial building. Processing and delivery usually takes 3-5 working days, with expedited turnaround available.' },
                ].map((faq, idx) => (
                  <div key={idx} className="p-6 bg-gray-50 rounded-xl">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h3>
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Get a Free, No-Obligation Quotation</h2>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Every project is different. Tell us about your space and requirements, and we will provide a detailed quotation within 24 hours. Visit our <Link to="/pricing" className="text-blue-600 hover:underline">pricing page</Link> for an overview of our packages.
              </p>
              <Link
                to="/"
                className="inline-flex px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Request a Quote
              </Link>
            </section>
          </div>
        </div>
      </article>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default MatterportScanCostPage;
